import React, { useRef } from "react";
import { Circle, Path, Polygon, Rect, Svg } from "react-native-svg";
import { useHover } from "react-native-web-hooks";
import { DefaultTheme, useTheme } from "styled-components/native";

export type IconProps = {
  size?: number;
  primaryFill: keyof DefaultTheme["colors"];
  secondaryFill?: keyof DefaultTheme["colors"];
  primaryFillHover?: keyof DefaultTheme["colors"];
  secondaryFillHover?: keyof DefaultTheme["colors"];
};

export const IconWrapper: React.FC<
  IconProps & { Icon: React.FC<{ p: string; s: string; theme: DefaultTheme }> }
> = props => {
  const ref = useRef(null);

  const isHovered = useHover(ref);
  const theme = useTheme();

  const size = props.size || 24;

  const p = isHovered
    ? theme.colors[props.primaryFillHover || props.primaryFill]
    : theme.colors[props.primaryFill];
  const s = isHovered
    ? theme.colors[
        props.secondaryFillHover || props.secondaryFill || props.primaryFill
      ]
    : theme.colors[props.secondaryFill || props.primaryFill];

  return (
    <Svg
      ref={ref}
      viewBox={`0 0 24 24`}
      height={size}
      width={size}
      style={{ flexShrink: 0 }}
    >
      <props.Icon p={p} s={s} theme={theme} />
    </Svg>
  );
};

export const HeartIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M12.88 8.88a3 3 0 1 1 4.24 4.24l-4.41 4.42a1 1 0 0 1-1.42 0l-4.41-4.42a3 3 0 1 1 4.24-4.24l.88.88.88-.88z"
        />
      </>
    )}
  />
);

export const StarIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M9.53 16.93a1 1 0 0 1-1.45-1.05l.47-2.76-2-1.95a1 1 0 0 1 .55-1.7l2.77-.4 1.23-2.51a1 1 0 0 1 1.8 0l1.23 2.5 2.77.4a1 1 0 0 1 .55 1.71l-2 1.95.47 2.76a1 1 0 0 1-1.45 1.05L12 15.63l-2.47 1.3z"
        />
      </>
    )}
  />
);

export const SparklesIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <Path
        fill={p}
        d="M12.1838 12.3239L10.1437 7.02047C10.1131 6.94088 10.0591 6.87244 9.98883 6.82417C9.91854 6.77589 9.83527 6.75005 9.75 6.75005C9.66473 6.75005 9.58146 6.77589 9.51117 6.82417C9.44088 6.87244 9.38686 6.94088 9.35625 7.02047L7.31625 12.3239C7.29506 12.379 7.26254 12.4291 7.2208 12.4708C7.17905 12.5125 7.12901 12.5451 7.07391 12.5662L1.77047 14.6063C1.69088 14.6369 1.62244 14.6909 1.57417 14.7612C1.52589 14.8315 1.50005 14.9147 1.50005 15C1.50005 15.0853 1.52589 15.1685 1.57417 15.2388C1.62244 15.3091 1.69088 15.3631 1.77047 15.3937L7.07391 17.4338C7.12901 17.4549 7.17905 17.4875 7.2208 17.5292C7.26254 17.5709 7.29506 17.621 7.31625 17.6761L9.35625 22.9795C9.38686 23.0591 9.44088 23.1276 9.51117 23.1758C9.58146 23.2241 9.66473 23.2499 9.75 23.2499C9.83527 23.2499 9.91854 23.2241 9.98883 23.1758C10.0591 23.1276 10.1131 23.0591 10.1437 22.9795L12.1838 17.6761C12.2049 17.621 12.2375 17.5709 12.2792 17.5292C12.3209 17.4875 12.371 17.4549 12.4261 17.4338L17.7295 15.3937C17.8091 15.3631 17.8776 15.3091 17.9258 15.2388C17.9741 15.1685 17.9999 15.0853 17.9999 15C17.9999 14.9147 17.9741 14.8315 17.9258 14.7612C17.8776 14.6909 17.8091 14.6369 17.7295 14.6063L12.4261 12.5662C12.371 12.5451 12.3209 12.5125 12.2792 12.4708C12.2375 12.4291 12.2049 12.379 12.1838 12.3239ZM5.0625 3.1875L4.125 0.75L3.1875 3.1875L0.75 4.125L3.1875 5.0625L4.125 7.5L5.0625 5.0625L7.5 4.125L5.0625 3.1875ZM20.0002 5.49984L18.75 2.25L17.4998 5.49984L14.25 6.75L17.4998 8.00016L18.75 11.25L20.0002 8.00016L23.25 6.75L20.0002 5.49984Z"
        stroke={s}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  />
);

export const DotsVerticalIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ s }) => (
      <Path
        fill={s}
        fillRule="evenodd"
        d="M12 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
      />
    )}
  />
);

export const AddCircleIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r={(props.size || 24) / 2} fill={p} />
        <Path
          fill={s}
          d="M13 11h4a1 1 0 0 1 0 2h-4v4a1 1 0 0 1-2 0v-4H7a1 1 0 0 1 0-2h4V7a1 1 0 0 1 2 0v4z"
        />
      </>
    )}
  />
);

export const PlayIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M15.51 11.14a1 1 0 0 1 0 1.72l-5 3A1 1 0 0 1 9 15V9a1 1 0 0 1 1.51-.86l5 3z"
        />
      </>
    )}
  />
);

export const PauseIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M9 8h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zm5 0h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"
        />
      </>
    )}
  />
);

export const CloseIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p }) => (
      <Path
        fill={p}
        d="M15.78 14.36a1 1 0 0 1-1.42 1.42l-2.82-2.83-2.83 2.83a1 1 0 1 1-1.42-1.42l2.83-2.82L7.3 8.7a1 1 0 0 1 1.42-1.42l2.83 2.83 2.82-2.83a1 1 0 0 1 1.42 1.42l-2.83 2.83 2.83 2.82z"
      />
    )}
  />
);

export const CloseSquareIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Rect width="18" height="18" x="3" y="3" rx="2" fill={p} />
        <Path
          fill={s}
          d="M13.41 12l2.83 2.83a1 1 0 0 1-1.41 1.41L12 13.41l-2.83 2.83a1 1 0 1 1-1.41-1.41L10.59 12 7.76 9.17a1 1 0 0 1 1.41-1.41L12 10.59l2.83-2.83a1 1 0 0 1 1.41 1.41L13.41 12z"
        />
      </>
    )}
  />
);

export const MonitorIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5h3a1 1 0 0 0 .83-.45L10 8.8l5.17 7.75a1 1 0 0 0 1.66 0l1.7-2.55H21z"
        />
        <Path
          fill={p}
          d="M21 12h-3a1 1 0 0 0-.83.45L16 14.2l-5.17-7.75a1 1 0 0 0-1.66 0L5.47 12H3V5c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v7z"
        />
      </>
    )}
  />
);

export const ViewVisibleIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M17.56 17.66a8 8 0 0 1-11.32 0L1.3 12.7a1 1 0 0 1 0-1.42l4.95-4.95a8 8 0 0 1 11.32 0l4.95 4.95a1 1 0 0 1 0 1.42l-4.95 4.95zM11.9 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z"
        />
        <Circle cx="12" cy="12" r="3" fill={s} />
      </>
    )}
  />
);

export const TagIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M2.59 13.41A1.98 1.98 0 0 1 2 12V7a5 5 0 0 1 5-5h4.99c.53 0 1.04.2 1.42.59l8 8a2 2 0 0 1 0 2.82l-8 8a2 2 0 0 1-2.82 0l-8-8zM7 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
        />
        <Path fill={s} d="M12 18l6-6-4-4-6 6.01L12 18z" />
      </>
    )}
  />
);

export const TargetIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M15.23 2.53l-.35.35a3 3 0 0 0-.8 1.4 8.01 8.01 0 1 0 5.64 5.63 3 3 0 0 0 1.4-.79l.35-.35A9.99 9.99 0 0 1 12 22a10 10 0 1 1 3.23-19.47zM13.55 6.2L11.75 8a4 4 0 1 0 4.24 4.25l1.8-1.8a6 6 0 1 1-4.24-4.25z"
        />
        <Path
          fill={s}
          d="M16 6.59V5a1 1 0 0 1 .3-.7l2-2A1 1 0 0 1 20 3v1h1a1 1 0 0 1 .7 1.7l-2 2a1 1 0 0 1-.7.3h-1.59l-4.7 4.7a1 1 0 0 1-1.42-1.4L16 6.58z"
        />
      </>
    )}
  />
);

export const HomeIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M9 22H5a1 1 0 0 1-1-1V11l8-8 8 8v10a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v4a1 1 0 0 1-1 1zm3-9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
        />
        <Path
          fill={s}
          d="M12.01 4.42l-8.3 8.3a1 1 0 1 1-1.42-1.41l9.02-9.02a1 1 0 0 1 1.41 0l8.99 9.02a1 1 0 0 1-1.42 1.41l-8.28-8.3z"
        />
      </>
    )}
  />
);

export const UserGroupIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M12 13a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v3a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1 1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-3a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3zM7 9a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm10 0a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"
        />
        <Path
          fill={s}
          d="M12 13a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm-3 1h6a3 3 0 0 1 3 3v3a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-3a3 3 0 0 1 3-3z"
        />
      </>
    )}
  />
);

export const VideoCamIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M13.59 12l6.7-6.7A1 1 0 0 1 22 6v12a1 1 0 0 1-1.7.7L13.58 12z"
        />
        <Rect width="14" height="14" x="2" y="5" fill={p} rx="2" />
      </>
    )}
  />
);

export const CollectionIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Rect width="20" height="12" x="2" y="10" fill={p} rx="2" />
        <Path
          fill={s}
          d="M20 8H4c0-1.1.9-2 2-2h12a2 2 0 0 1 2 2zm-2-4H6c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z"
        />
      </>
    )}
  />
);

export const WidgetAddIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M5 13h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4c0-1.1.9-2 2-2zm10 0h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4c0-1.1.9-2 2-2zM5 3h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2z"
        />
        <Path
          fill={s}
          d="M18 6h2a1 1 0 0 1 0 2h-2v2a1 1 0 0 1-2 0V8h-2a1 1 0 0 1 0-2h2V4a1 1 0 0 1 2 0v2z"
        />
      </>
    )}
  />
);

export const WalletIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M2 5c0 1.1.9 2 2 2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5zm16 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
        />
        <Path fill={s} d="M4 3h11a2 2 0 0 1 2 2v2H4a2 2 0 1 1 0-4z" />
      </>
    )}
  />
);

export const SettingsIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M6.8 3.45c.87-.52 1.82-.92 2.83-1.17a2.5 2.5 0 0 0 4.74 0c1.01.25 1.96.65 2.82 1.17a2.5 2.5 0 0 0 3.36 3.36c.52.86.92 1.8 1.17 2.82a2.5 2.5 0 0 0 0 4.74c-.25 1.01-.65 1.96-1.17 2.82a2.5 2.5 0 0 0-3.36 3.36c-.86.52-1.8.92-2.82 1.17a2.5 2.5 0 0 0-4.74 0c-1.01-.25-1.96-.65-2.82-1.17a2.5 2.5 0 0 0-3.36-3.36 9.94 9.94 0 0 1-1.17-2.82 2.5 2.5 0 0 0 0-4.74c.25-1.01.65-1.96 1.17-2.82a2.5 2.5 0 0 0 3.36-3.36zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
        />
        <Circle cx="12" cy="12" r="2" fill={s} />
      </>
    )}
  />
);

export const AlertIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="19" r="3" fill={s} />
        <Path
          fill={p}
          d="M10.02 4.28L10 4a2 2 0 1 1 3.98.28A7 7 0 0 1 19 11v5a1 1 0 0 0 1 1 1 1 0 0 1 0 2H4a1 1 0 0 1 0-2 1 1 0 0 0 1-1v-5a7 7 0 0 1 5.02-6.72z"
        />
      </>
    )}
  />
);

export const FilterIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2zm11 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-8 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
        />
        <Path
          fill={s}
          d="M9.73 14H17a1 1 0 0 1 0 2H9.73a2 2 0 0 0 0-2zm4.54-6a2 2 0 0 0 0 2H7a1 1 0 1 1 0-2h7.27z"
        />
      </>
    )}
  />
);

export const SortAscIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M18 13v7a1 1 0 0 1-2 0v-7h-3a1 1 0 0 1-.7-1.7l4-4a1 1 0 0 1 1.4 0l4 4A1 1 0 0 1 21 13h-3z"
        />
        <Path
          fill={p}
          d="M3 3h13a1 1 0 0 1 0 2H3a1 1 0 1 1 0-2zm0 4h9a1 1 0 0 1 0 2H3a1 1 0 1 1 0-2zm0 4h5a1 1 0 0 1 0 2H3a1 1 0 0 1 0-2z"
        />
      </>
    )}
  />
);

export const SortDescIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M6 11V4a1 1 0 1 1 2 0v7h3a1 1 0 0 1 .7 1.7l-4 4a1 1 0 0 1-1.4 0l-4-4A1 1 0 0 1 3 11h3z"
        />
        <Path
          fill={p}
          d="M21 21H8a1 1 0 0 1 0-2h13a1 1 0 0 1 0 2zm0-4h-9a1 1 0 0 1 0-2h9a1 1 0 0 1 0 2zm0-4h-5a1 1 0 0 1 0-2h5a1 1 0 0 1 0 2z"
        />
      </>
    )}
  />
);

export const SearchIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="10" cy="10" r="7" fill={p} />
        <Path
          fill={s}
          d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"
        />
      </>
    )}
  />
);

export const AddIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          fillRule="evenodd"
          d="M17 11a1 1 0 0 1 0 2h-4v4a1 1 0 0 1-2 0v-4H7a1 1 0 0 1 0-2h4V7a1 1 0 0 1 2 0v4h4z"
        />
      </>
    )}
  />
);

export const HardDriveIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2zm8.12 7.47A5 5 0 1 0 17 14V9a1 1 0 0 0-1.7-.7l-1.18 1.17z"
        />
        <Path
          fill={s}
          d="M12 16a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm6-11a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM6 5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM6 21a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM8 6h4a1 1 0 0 1 0 2H8a1 1 0 1 1 0-2z"
        />
      </>
    )}
  />
);

export const PokeBallIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s, theme }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Rect
          width="20"
          height="4"
          x="2"
          y="10"
          fill={theme.colors.background}
          rx="1"
        />
        <Circle cx="12" cy="12" r="6" fill={theme.colors.background} />
        <Circle cx="12" cy="12" r="3" fill={s} />
      </>
    )}
  />
);

export const ReceiptIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M9 18.41l-2.3 2.3a1 1 0 0 1-1.4 0l-2-2A1 1 0 0 1 3 18V5c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v13a1 1 0 0 1-.3.7l-2 2a1 1 0 0 1-1.4 0L15 18.42l-2.3 2.3a1 1 0 0 1-1.4 0L9 18.4z"
        />
        <Path
          fill={s}
          d="M7 7h10a1 1 0 0 1 0 2H7a1 1 0 1 1 0-2zm0 4h10a1 1 0 0 1 0 2H7a1 1 0 0 1 0-2z"
        />
      </>
    )}
  />
);

export const CheckIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M10 14.59l6.3-6.3a1 1 0 0 1 1.4 1.42l-7 7a1 1 0 0 1-1.4 0l-3-3a1 1 0 0 1 1.4-1.42l2.3 2.3z"
        />
      </>
    )}
  />
);

export const RefreshIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Circle cx="12" cy="12" r="10" fill={p} />
        <Path
          fill={s}
          d="M8.52 7.11a5.98 5.98 0 0 1 8.98 2.5 1 1 0 1 1-1.83.8 4 4 0 0 0-5.7-1.86l.74.74A1 1 0 0 1 10 11H7a1 1 0 0 1-1-1V7a1 1 0 0 1 1.7-.7l.82.81zm5.51 8.34l-.74-.74A1 1 0 0 1 14 13h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1.7.7l-.82-.81A5.98 5.98 0 0 1 6.5 14.4a1 1 0 1 1 1.83-.8 4 4 0 0 0 5.7 1.85z"
        />
      </>
    )}
  />
);

export const LinkIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M19.48 13.03l-.02-.03a1 1 0 1 1 1.75-.98A6 6 0 0 1 16 21h-4a6 6 0 1 1 0-12h1a1 1 0 0 1 0 2h-1a4 4 0 1 0 0 8h4a4 4 0 0 0 3.48-5.97z"
        />
        <Path
          fill={p}
          d="M4.52 10.97l.02.03a1 1 0 1 1-1.75.98A6 6 0 0 1 8 3h4a6 6 0 1 1 0 12h-1a1 1 0 0 1 0-2h1a4 4 0 1 0 0-8H8a4 4 0 0 0-3.48 5.97z"
        />
      </>
    )}
  />
);

export const RewindIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M22 5a1 1 0 0 0-1.5-.86l-12 7a1 1 0 0 0 0 1.72l12 7A1 1 0 0 0 22 19V5z"
        />
        <Path
          fill={p}
          d="M15 5a1 1 0 0 0-1.5-.86l-12 7a1 1 0 0 0 0 1.72l12 7A1 1 0 0 0 15 19V5z"
        />
      </>
    )}
  />
);

export const TrashIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M5 5h14l-.89 15.12a2 2 0 0 1-2 1.88H7.9a2 2 0 0 1-2-1.88L5 5zm5 5a1 1 0 0 0-1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0-1-1z"
        />
        <Path
          fill={s}
          d="M8.59 4l1.7-1.7A1 1 0 0 1 11 2h2a1 1 0 0 1 .7.3L15.42 4H19a1 1 0 0 1 0 2H5a1 1 0 1 1 0-2h3.59z"
        />
      </>
    )}
  />
);

export const SignIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M14 12h5l3 3-3 3h-5v4h-4v-4H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h6v-1h4v1z"
        />
        <Path
          fill={s}
          d="M10 4a2 2 0 1 1 4 0h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5L2 7l3-3h5z"
        />
      </>
    )}
  />
);

export const UploadIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M8 4a1 1 0 0 1-1 1H5v10h2a2 2 0 0 1 2 2c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h2V5h-2a1 1 0 0 1 0-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h2a1 1 0 0 1 1 1z"
        />
        <Path
          fill={s}
          d="M11 6.41V13a1 1 0 0 0 2 0V6.41l1.3 1.3a1 1 0 0 0 1.4-1.42l-3-3a1 1 0 0 0-1.4 0l-3 3a1 1 0 0 0 1.4 1.42L11 6.4z"
        />
      </>
    )}
  />
);

export const EditIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M4 14a1 1 0 0 1 .3-.7l11-11a1 1 0 0 1 1.4 0l3 3a1 1 0 0 1 0 1.4l-11 11a1 1 0 0 1-.7.3H5a1 1 0 0 1-1-1v-3z"
        />
        <Rect width="20" height="2" x="2" y="20" fill={s} rx="1" />
      </>
    )}
  />
);

export const BackIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M9.41 11H17a1 1 0 0 1 0 2H9.41l2.3 2.3a1 1 0 1 1-1.42 1.4l-4-4a1 1 0 0 1 0-1.4l4-4a1 1 0 0 1 1.42 1.4L9.4 11z"
        />
      </>
    )}
  />
);

export const FileAddIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M6 2h6v6c0 1.1.9 2 2 2h6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2z"
        />
        <Path
          fill="white"
          d="M13 15h2a1 1 0 0 1 0 2h-2v2a1 1 0 0 1-2 0v-2H9a1 1 0 0 1 0-2h2v-2a1 1 0 0 1 2 0v2z"
        />
        <Polygon fill={s} points="14 2 20 8 14 8" />
      </>
    )}
  />
);

export const FolderAddIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M4 4h7l2 2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2z"
        />
        <Rect width="20" height="12" x="2" y="8" fill={p} rx="2" />
        <Path
          fill="white"
          d="M13 13h2a1 1 0 0 1 0 2h-2v2a1 1 0 0 1-2 0v-2H9a1 1 0 0 1 0-2h2v-2a1 1 0 0 1 2 0v2z"
        />
      </>
    )}
  />
);

export const UndoIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M6.55 6.14l1.16 1.15A1 1 0 0 1 7 9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1.7-.7l1.44 1.42A10 10 0 1 1 2 12a1 1 0 0 1 2 0 8 8 0 1 0 2.55-5.86z"
        />
      </>
    )}
  />
);

export const EnterIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M13 4V3a1 1 0 0 1 1.27-.96l6.98 2A1 1 0 0 1 22 5v14a1 1 0 0 1-.75.97l-6.98 2A1 1 0 0 1 13 21v-1h-3a1 1 0 0 1-1-1v-2a1 1 0 0 1 2 0v1h2V6h-2v1a1 1 0 0 1-2 0V5a1 1 0 0 1 1-1h3z"
        />
        <Path
          fill={s}
          d="M7.59 11l-1.3-1.3c-.94-.94.47-2.35 1.42-1.4l3 3a1 1 0 0 1 0 1.4l-3 3c-.95.95-2.36-.46-1.42-1.4L7.6 13H3a1 1 0 0 1 0-2h4.59z"
        />
      </>
    )}
  />
);

export const ExitIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M11 4h3a1 1 0 0 1 1 1v3a1 1 0 0 1-2 0V6h-2v12h2v-2a1 1 0 0 1 2 0v3a1 1 0 0 1-1 1h-3v1a1 1 0 0 1-1.27.96l-6.98-2A1 1 0 0 1 2 19V5a1 1 0 0 1 .75-.97l6.98-2A1 1 0 0 1 11 3v1z"
        />
        <Path
          fill={s}
          d="M18.59 11l-1.3-1.3c-.94-.94.47-2.35 1.42-1.4l3 3a1 1 0 0 1 0 1.4l-3 3c-.95.95-2.36-.46-1.42-1.4l1.3-1.3H14a1 1 0 0 1 0-2h4.59z"
        />
      </>
    )}
  />
);

export const ExternalIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={p}
          d="M12 8a1 1 0 0 1-1 1H5v10h10v-6a1 1 0 0 1 2 0v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9c0-1.1.9-2 2-2h6a1 1 0 0 1 1 1z"
        />
        <Path
          fill={s}
          d="M19 6.41L8.7 16.71a1 1 0 1 1-1.4-1.42L17.58 5H14a1 1 0 0 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0V6.41z"
        />
      </>
    )}
  />
);

export const DashboardIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          fill={s}
          d="M3 11h2a1 1 0 0 1 0 2H3v-2zm3.34-6.07l1.42 1.41a1 1 0 0 1-1.42 1.42L4.93 6.34l1.41-1.41zM13 3v2a1 1 0 0 1-2 0V3h2zm6.07 3.34l-1.41 1.42a1 1 0 1 1-1.42-1.42l1.42-1.41 1.41 1.41zM21 13h-2a1 1 0 0 1 0-2h2v2z"
        />
        <Path
          fill={p}
          d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20zm-6.93-6h13.86a8 8 0 1 0-13.86 0z"
        />
        <Path fill={s} d="M11 14.27V9a1 1 0 0 1 2 0v5.27a2 2 0 1 1-2 0z" />
      </>
    )}
  />
);

export const SunIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <Path
        fill={p}
        d="M12 2C12.5523 2 13 2.44772 13 3V4C13 4.55228 12.5523 5 12 5C11.4477 5 11 4.55228 11 4V3C11 2.44772 11.4477 2 12 2ZM4.92889 4.92889C5.31942 4.53837 5.95258 4.53837 6.34311 4.92889L7.05011 5.63589C7.44063 6.02642 7.44063 6.65958 7.05011 7.05011C6.65958 7.44063 6.02642 7.44063 5.63589 7.05011L4.92889 6.34311C4.53837 5.95258 4.53837 5.31942 4.92889 4.92889ZM19.0711 4.92889C19.4616 5.31942 19.4616 5.95258 19.0711 6.34311L18.3641 7.05011C17.9736 7.44063 17.3404 7.44063 16.9499 7.05011C16.5594 6.65958 16.5594 6.02642 16.9499 5.63589L17.6569 4.92889C18.0474 4.53837 18.6806 4.53837 19.0711 4.92889ZM12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9ZM8.46447 8.46447C9.40215 7.52678 10.6739 7 12 7C13.3261 7 14.5979 7.52678 15.5355 8.46447C16.4732 9.40215 17 10.6739 17 12C17 13.3261 16.4732 14.5979 15.5355 15.5355C14.5979 16.4732 13.3261 17 12 17C10.6739 17 9.40215 16.4732 8.46447 15.5355C7.52678 14.5979 7 13.3261 7 12C7 10.6739 7.52678 9.40215 8.46447 8.46447ZM2 12C2 11.4477 2.44772 11 3 11H4C4.55228 11 5 11.4477 5 12C5 12.5523 4.55228 13 4 13H3C2.44772 13 2 12.5523 2 12ZM19 12C19 11.4477 19.4477 11 20 11H21C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H20C19.4477 13 19 12.5523 19 12ZM7.05011 16.9499C7.44063 17.3404 7.44063 17.9736 7.05011 18.3641L6.34311 19.0711C5.95258 19.4616 5.31942 19.4616 4.92889 19.0711C4.53837 18.6806 4.53837 18.0474 4.92889 17.6569L5.63589 16.9499C6.02642 16.5594 6.65958 16.5594 7.05011 16.9499ZM16.9499 16.9499C17.3404 16.5594 17.9736 16.5594 18.3641 16.9499L19.0711 17.6569C19.4616 18.0474 19.4616 18.6806 19.0711 19.0711C18.6806 19.4616 18.0474 19.4616 17.6569 19.0711L16.9499 18.3641C16.5594 17.9736 16.5594 17.3404 16.9499 16.9499ZM12 19C12.5523 19 13 19.4477 13 20V21C13 21.5523 12.5523 22 12 22C11.4477 22 11 21.5523 11 21V20C11 19.4477 11.4477 19 12 19Z"
      />
    )}
  />
);
export const MoonIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <Path
        fill={p}
        d="M9.3529 2.93872C9.63639 3.22204 9.72332 3.64726 9.57379 4.01912C8.98872 5.47403 8.84414 7.06881 9.15803 8.60522C9.47192 10.1416 10.2304 11.5519 11.3393 12.6607C12.4481 13.7696 13.8584 14.5281 15.3948 14.842C16.9312 15.1559 18.526 15.0113 19.9809 14.4262C20.3526 14.2768 20.7777 14.3636 21.061 14.6468C21.3444 14.9301 21.4313 15.3551 21.2819 15.7269C20.5374 17.5794 19.2556 19.1668 17.6015 20.2848C15.9473 21.4027 13.9965 22.0001 12 22H11.9989C9.67719 21.9975 7.42865 21.1875 5.63874 19.7087C3.84883 18.23 2.62904 16.1747 2.18848 13.8952C1.74791 11.6156 2.11401 9.25384 3.22401 7.21465C4.33401 5.17546 6.11879 3.5859 8.27237 2.71844C8.64414 2.5687 9.06941 2.65539 9.3529 2.93872ZM7.08276 5.69044C6.22583 6.35993 5.50803 7.20195 4.98063 8.17084C4.09256 9.80231 3.79966 11.6919 4.15214 13.5157C4.50462 15.3394 5.48052 16.9838 6.91256 18.1669C8.34459 19.3499 10.1436 19.998 12.0011 20C13.598 19.9999 15.1584 19.5219 16.4816 18.6277C17.1818 18.1545 17.7986 17.5761 18.3124 16.9169C17.2134 17.0628 16.092 17.0257 14.9945 16.8015C13.074 16.4092 11.3111 15.461 9.92506 14.075C8.53901 12.6889 7.59087 10.9261 7.19851 9.00555C6.97447 7.90893 6.93728 6.78851 7.08276 5.69044Z"
      />
    )}
  />
);

export const ShuffleIcon: React.FC<IconProps> = props => (
  <IconWrapper
    {...props}
    Icon={({ p, s }) => (
      <>
        <Path
          d="M0.75 7C0.75 6.30964 1.30964 5.75 2 5.75H5C8.45036 5.75 11.25 8.54964 11.25 12C11.25 15.4504 8.45036 18.25 5 18.25H2C1.30964 18.25 0.75 17.6904 0.75 17C0.75 16.3096 1.30964 15.75 2 15.75H5C7.06964 15.75 8.75 14.0696 8.75 12C8.75 9.93036 7.06964 8.25 5 8.25H2C1.30964 8.25 0.75 7.69036 0.75 7Z"
          fill={p}
        />
        <Path
          d="M15 8.25C12.9304 8.25 11.25 9.93036 11.25 12C11.25 14.0696 12.9304 15.75 15 15.75H21C21.6904 15.75 22.25 16.3096 22.25 17C22.25 17.6904 21.6904 18.25 21 18.25H15C11.5496 18.25 8.75 15.4504 8.75 12C8.75 8.54964 11.5496 5.75 15 5.75H21C21.6904 5.75 22.25 6.30964 22.25 7C22.25 7.69036 21.6904 8.25 21 8.25H15Z"
          fill={p}
        />
        <Path
          d="M17.75 3.49463L23.3585 6.99994L17.75 10.5052V3.49463Z"
          fill={p}
        />
        <Path
          d="M17.75 13.4946L23.3585 16.9999L17.75 20.5052V13.4946Z"
          fill={p}
        />
      </>
    )}
  />
);
